import React, { useState } from 'react';

// ── Chakra definitions ─────────────────────────────────────
const CHAKRA_GROUPS = [
  {
    key: 'major',
    label: 'Major Chakras',
    color: '#1a6b5a',
    chakras: [
      'Basic', 'Sex', 'Navel', 'Meng Mein',
      'Front Spleen', 'Back Spleen',
      'Front Solar Plexus', 'Back Solar Plexus',
      'Front Heart', 'Back Heart',
      'Throat', 'Ajna', 'Forehead', 'Crown'
    ]
  },
  {
    key: 'minor',
    label: 'Minor Chakras',
    color: '#2980b9',
    chakras: [
      'Back Head', 'Left Temple', 'Right Temple',
      'Left Jaw', 'Right Jaw', 'Secondary Throat',
      'Left Hand', 'Right Hand',
      'Left Sole', 'Right Sole', 'Perineum'
    ]
  },
  {
    key: 'organs',
    label: 'Organs & Mini Chakras',
    color: '#8e44ad',
    chakras: [
      'Left Eye', 'Right Eye', 'Left Ear', 'Right Ear',
      'Nose', 'Mouth', 'Left Lung', 'Right Lung',
      'Liver', 'Stomach', 'Large Intestine', 'Small Intestine',
      'Left Kidney', 'Right Kidney', 'Bladder', 'Reproductive'
    ]
  }
];

// Energy: C (Congested), D (Depleted), N (Normal)
const ENERGY_OPTIONS = [
  { value: 'C', label: 'C', title: 'Congested', bg: '#e67e22', color: '#fff' },
  { value: 'D', label: 'D', title: 'Depleted',  bg: '#7f8c8d', color: '#fff' },
  { value: 'N', label: 'N', title: 'Normal',    bg: '#27ae60', color: '#fff' },
];

// Activation: U (Underactivated), O (Overactivated), N (Normal)
const ACTIVATION_OPTIONS = [
  { value: 'U', label: 'U', title: 'Underactivated', bg: '#2980b9', color: '#fff' },
  { value: 'O', label: 'O', title: 'Overactivated',  bg: '#e74c3c', color: '#fff' },
  { value: 'N', label: 'N', title: 'Normal',         bg: '#27ae60', color: '#fff' },
];

function buildInitialResults() {
  var results = {};
  CHAKRA_GROUPS.forEach(function(g) {
    g.chakras.forEach(function(chakra) {
      results[g.key + '_' + chakra] = { chakra: chakra, group: g.key, energyBefore: '', energyAfter: '', activationBefore: '', activationAfter: '' };
    });
  });
  return results;
}

function PillSelector({ options, value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: '3px' }}>
      {options.map(function(opt) {
        var selected = value === opt.value;
        return (
          <button key={opt.value} type="button" title={opt.title}
            onClick={function() { onChange(selected ? '' : opt.value); }}
            style={{
              width: '26px', height: '26px', borderRadius: '50%',
              border: selected ? '2px solid ' + opt.bg : '1px solid #ddd',
              background: selected ? opt.bg : '#f9f9f9',
              color: selected ? opt.color : '#999',
              fontSize: '10px', fontWeight: '700', cursor: 'pointer',
              fontFamily: "'Raleway', sans-serif",
              transition: 'all 0.15s ease',
              flexShrink: 0
            }}>
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const ChakraScanForm = ({ onChange }) => {
  const [results, setResults] = useState(buildInitialResults);
  const [openGroups, setOpenGroups] = useState({ major: true, minor: false, organs: false });

  function update(key, field, val) {
    var updated = Object.assign({}, results, { [key]: Object.assign({}, results[key], { [field]: val }) });
    setResults(updated);
    // Pass only rows with at least one value filled
    var flat = Object.values(updated).filter(function(r) {
      return r.energyBefore || r.energyAfter || r.activationBefore || r.activationAfter;
    });
    onChange(flat);
  }

  function toggleGroup(key) {
    setOpenGroups(function(prev) { return Object.assign({}, prev, { [key]: !prev[key] }); });
  }

  return (
    <div style={{ marginTop: '8px' }}>
      {/* Legend */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '10px 14px', background: '#f5f8f7', borderRadius: '8px', marginBottom: '12px', fontSize: '11px', fontWeight: '700', fontFamily: "'Raleway', sans-serif" }}>
        <span style={{ color: '#555', letterSpacing: '0.5px' }}>ENERGY LEVEL:</span>
        {ENERGY_OPTIONS.map(function(o) {
          return <span key={o.value} style={{ color: o.bg }}>{o.value} = {o.title}</span>;
        })}
        <span style={{ color: '#555', letterSpacing: '0.5px', marginLeft: '8px' }}>ACTIVATION:</span>
        {ACTIVATION_OPTIONS.map(function(o) {
          return <span key={o.value} style={{ color: o.bg }}>{o.value} = {o.title}</span>;
        })}
      </div>

      {/* Column headers */}
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr 1fr 1fr', gap: '4px', padding: '6px 12px', background: '#1a6b5a', borderRadius: '6px 6px 0 0', marginBottom: '1px' }}>
        <div style={{ fontSize: '10px', fontWeight: '700', color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Chakra</div>
        {[
          { label: 'Energy Before', sub: 'C / D / N' },
          { label: 'Energy After',  sub: 'C / D / N' },
          { label: 'Activation Before', sub: 'U / O / N' },
          { label: 'Activation After',  sub: 'U / O / N' },
        ].map(function(col) {
          return (
            <div key={col.label} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '10px', fontWeight: '700', color: '#fff', letterSpacing: '0.5px' }}>{col.label}</div>
              <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)' }}>{col.sub}</div>
            </div>
          );
        })}
      </div>

      {/* Groups */}
      {CHAKRA_GROUPS.map(function(group) {
        var isOpen = openGroups[group.key];
        return (
          <div key={group.key} style={{ marginBottom: '4px', border: '1px solid #e8e8e8', borderRadius: '6px', overflow: 'hidden' }}>
            {/* Group header */}
            <button type="button" onClick={function() { toggleGroup(group.key); }}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: isOpen ? group.color : '#f5f5f5', border: 'none', cursor: 'pointer', transition: 'background 0.2s' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: isOpen ? '#fff' : group.color }}></div>
                <span style={{ fontSize: '12px', fontWeight: '700', fontFamily: "'Raleway', sans-serif", color: isOpen ? '#fff' : '#333', letterSpacing: '0.5px' }}>{group.label}</span>
                <span style={{ fontSize: '11px', color: isOpen ? 'rgba(255,255,255,0.6)' : '#999' }}>({group.chakras.length} chakras)</span>
              </div>
              <span style={{ fontSize: '14px', color: isOpen ? '#fff' : '#999' }}>{isOpen ? '▲' : '▼'}</span>
            </button>

            {/* Rows */}
            {isOpen && (
              <div>
                {group.chakras.map(function(chakra, idx) {
                  var key = group.key + '_' + chakra;
                  var row = results[key];
                  var isEven = idx % 2 === 0;
                  return (
                    <div key={chakra} style={{ display: 'grid', gridTemplateColumns: '140px 1fr 1fr 1fr 1fr', gap: '4px', padding: '8px 12px', background: isEven ? '#fff' : '#fafafa', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{ fontSize: '12px', color: '#333', fontWeight: '500' }}>{chakra}</div>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <PillSelector options={ENERGY_OPTIONS} value={row.energyBefore} onChange={function(v) { update(key, 'energyBefore', v); }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <PillSelector options={ENERGY_OPTIONS} value={row.energyAfter} onChange={function(v) { update(key, 'energyAfter', v); }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <PillSelector options={ACTIVATION_OPTIONS} value={row.activationBefore} onChange={function(v) { update(key, 'activationBefore', v); }} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <PillSelector options={ACTIVATION_OPTIONS} value={row.activationAfter} onChange={function(v) { update(key, 'activationAfter', v); }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ChakraScanForm;
